import { apiFetch } from './client';

export interface ModuleListEntry {
  code: string;
  name: string;
  description: string | null;
}

// Aggregate state for the Data Mapping settings page: one row with counts +
// last-upload timestamps for each of the four uploadable artifacts.
export interface DataMappingState {
  system_schema_id: string | null;
  system_schema_id_str: string | null;
  system_schema_version: string | null;
  system_schema_system_name: string | null;
  system_uploaded_at: string | null;
  system_objects: number;
  system_fields: number;

  staging_schema_id: string | null;
  staging_schema_id_str: string | null;
  staging_schema_version: string | null;
  staging_schema_system_name: string | null;
  staging_uploaded_at: string | null;
  staging_objects: number;
  staging_fields: number;

  sys_to_stg_uploaded_at: string | null;
  sys_to_stg_object_mappings: number;
  sys_to_stg_field_mappings: number;

  stg_to_cv_uploaded_at: string | null;
  stg_to_cv_object_mappings: number;
  stg_to_cv_field_mappings: number;

  // ClientVerse Core Extensions
  core_ext_schema_id: string | null;
  core_ext_schema_version: string | null;
  core_ext_uploaded_at: string | null;
  core_ext_objects_client_only: number;
  core_ext_fields_extending_core: number;
  core_ext_fields_client_only: number;
}

export interface CoreExtensionsUploadBody {
  schema_id?: string | null;
  schema_version?: string | null;
  description?: string | null;
  objects: Array<{
    name: string;
    label?: string | null;
    is_custom?: boolean;
    queryable?: boolean;
    fields?: Array<{
      name: string;
      label?: string | null;
      data_type?: string | null;
      is_required?: boolean;
      is_custom?: boolean;
      length?: number | null;
      reference_to?: string[];
      picklist_values?: Array<{
        value: string;
        label?: string | null;
        is_active?: boolean;
        is_default?: boolean;
        sort_order?: number;
      }>;
    }>;
  }>;
}

export interface CoreExtensionsImportCounts {
  objects_client_only: number;
  fields_extending_core: number;
  fields_client_only: number;
}

export interface SourceSchemaUploadBody {
  schema_id?: string | null;
  schema_version?: string | null;
  system_name?: string | null;
  description?: string | null;
  objects: Array<{
    name: string;
    label?: string | null;
    is_custom?: boolean;
    queryable?: boolean;
    description?: string | null;
    tier?: number | null;
    sf_target?: string | null;
    source?: string | null;
    fields?: Array<{
      name: string;
      label?: string | null;
      data_type?: string | null;
      sql_type?: string | null;
      is_required?: boolean;
      is_custom?: boolean;
      is_primary_key?: boolean;
      is_identity?: boolean;
      length?: number | null;
      reference_to?: string[];
      picklist_values?: Array<{
        value: string;
        label?: string | null;
        is_active?: boolean;
        is_default?: boolean;
        sort_order?: number;
      }>;
      source_expression?: string | null;
      description?: string | null;
    }>;
  }>;
}

export interface SchemaMappingUploadBody {
  mapping_id?: string | null;
  source_layer?: string | null;
  target_layer?: string | null;
  source_schema_id?: string | null;
  target_schema_id?: string | null;
  description?: string | null;
  join_key_fields?: string[];
  object_mappings: Array<{
    source_object: string;
    target_object: string;
    note?: string | null;
    filter?: string | null;
    load_pattern?: string | null;
    field_mappings?: Array<{
      source_fields?: string[];
      target_field?: string | null;
      transform?: string | null;
      confidence?: string | null;
      notes?: string | null;
    }>;
  }>;
}

export interface SourceSchemaImportCounts {
  layer: 'system' | 'staging';
  objects_loaded: number;
  fields_loaded: number;
}

export interface MappingImportCounts {
  mapping_key: string;
  object_mappings_loaded: number;
  field_mappings_loaded: number;
}

export type DataMappingSchemaLayer = 'system' | 'staging';
export type DataMappingMappingKey  = 'system-to-staging' | 'staging-to-clientverse';

export const adminModulesApi = {
  list: (t: string | null) =>
    apiFetch<ModuleListEntry[]>('/admin/modules', t),

  // Per-client Data Mapping state + uploads. ClientVerse Core stays global
  // (managed by the SF sync); these endpoints cover the four per-client
  // artifacts: System schema, Staging schema, and the two mapping JSONs.
  getDataMappingState: (t: string | null, clientId: string) =>
    apiFetch<DataMappingState>(`/admin/clients/${clientId}/data-mapping/state`, t),

  uploadSchema: (t: string | null, clientId: string, layer: DataMappingSchemaLayer, body: SourceSchemaUploadBody) =>
    apiFetch<SourceSchemaImportCounts>(`/admin/clients/${clientId}/data-mapping/schemas/${layer}`, t, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  uploadMapping: (t: string | null, clientId: string, key: DataMappingMappingKey, body: SchemaMappingUploadBody) =>
    apiFetch<MappingImportCounts>(`/admin/clients/${clientId}/data-mapping/mappings/${key}`, t, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Export endpoints — return the stored artifact in the exact shape of the
  // upload body so a downloaded file round-trips through the upload endpoint.
  exportSchema: (t: string | null, clientId: string, layer: DataMappingSchemaLayer) =>
    apiFetch<SourceSchemaUploadBody>(`/admin/clients/${clientId}/data-mapping/schemas/${layer}`, t),

  exportMapping: (t: string | null, clientId: string, key: DataMappingMappingKey) =>
    apiFetch<SchemaMappingUploadBody>(`/admin/clients/${clientId}/data-mapping/mappings/${key}`, t),

  uploadCoreExtensions: (t: string | null, clientId: string, body: CoreExtensionsUploadBody) =>
    apiFetch<CoreExtensionsImportCounts>(`/admin/clients/${clientId}/data-mapping/core-extensions`, t, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  exportCoreExtensions: (t: string | null, clientId: string) =>
    apiFetch<CoreExtensionsUploadBody>(`/admin/clients/${clientId}/data-mapping/core-extensions`, t),

  // Admin-side read of the client's Tech Inventory. Read-only — writes still
  // happen through the customer-portal endpoint used by the module page itself.
  listTechInventory: (t: string | null, clientId: string) =>
    apiFetch<AdminTechAsset[]>(`/admin/clients/${clientId}/tech-inventory`, t),

  // Reports > Systems matrix. One row per (client, asset). Clients with no
  // assets still return one row with null asset fields so they appear in the
  // matrix (empty across the board).
  reportsTechInventory: (t: string | null) =>
    apiFetch<TechInventoryReportRow[]>(`/admin/reports/tech-inventory`, t),
};

export interface TechInventoryReportRow {
  client_id: string;
  client_name: string;
  client_slug: string;
  // Asset fields are nullable — a client with zero assets still returns one
  // row via the LEFT JOIN so it shows up in the matrix.
  id: string | null;
  name: string | null;
  category: string | null;
  vendor: string | null;
  version: string | null;
  phase: 'MVP' | 'Post-MVP' | null;
  owner: string | null;
  status: 'active' | 'retiring' | 'planned' | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// Kept intentionally distinct from the customer-portal TechAsset type so this
// file has no circular dep on the module code — same shape, different owner.
export interface AdminTechAsset {
  id: string;
  name: string | null;
  category: string;
  vendor: string | null;
  version: string | null;
  phase: 'MVP' | 'Post-MVP' | null;
  owner: string | null;
  status: 'active' | 'retiring' | 'planned';
  notes: string | null;
  data_supplied: string[];
  staging_tables: string[];
  created_at: string;
  updated_at: string;
}
